import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../../api/apiClient.js";
import { format, parseISO } from "date-fns";

// Fetch available dates with recordings
export const fetchRecordingDates = createAsyncThunk(
  "recordings/fetchDates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/recordings/dates");
      return response.data.dates;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch recording dates!"
      );
    }
  }
);

// Fetch recordings for a specific date
export const fetchRecordings = createAsyncThunk(
  "recordings/fetchByDate",
  async (date, { rejectWithValue }) => {
    try {
      const dateObj = parseISO(date);
      const year = format(dateObj, "yyyy");
      const month = format(dateObj, "MM");
      const day = format(dateObj, "dd");

      const response = await apiClient.get(
        `/recordings/list/${year}/${month}/${day}`
      );
      return response.data.recordings;
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch recordings for this date!"
      );
    }
  }
);

// Fetch recordings for a date range
export const fetchRecordingsByRange = createAsyncThunk(
  "recordings/fetchByRange",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const startDateStr = format(parseISO(startDate), "yyyy-MM-dd");
      const endDateStr = format(parseISO(endDate), "yyyy-MM-dd");

      const response = await apiClient.get(
        `/recordings/list-range?startDate=${startDateStr}&endDate=${endDateStr}`
      );
      return response.data.recordings;
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch recordings for this date range!"
      );
    }
  }
);

// Rate a recording
export const rateRecording = createAsyncThunk(
  "recordings/rate",
  async ({ date, filename, rating, notes }, { rejectWithValue }) => {
    try {
      const dateObj = parseISO(date);
      const year = format(dateObj, "yyyy");
      const month = format(dateObj, "MM");
      const day = format(dateObj, "dd");

      const response = await apiClient.post(
        `/recordings/rate/${year}/${month}/${day}/${filename}`,
        { rating, notes }
      );

      return {
        success: response.data.success,
        filename,
        rating,
        notes,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to rate recording!"
      );
    }
  }
);

// Initial State
const initialState = {
  dates: [],
  recordings: [],
  selectedDate: new Date().toISOString(),
  startDate: new Date().toISOString(),
  endDate: new Date().toISOString(),
  loading: false,
  error: null,
};

const recordingsSlice = createSlice({
  name: "recordings",
  initialState,
  reducers: {
    setSelectedDate: (state, action) => {
      if (action.payload instanceof Date) {
        state.selectedDate = action.payload.toISOString();
      } else if (typeof action.payload === "string") {
        try {
          state.selectedDate = new Date(action.payload).toISOString();
        } catch (e) {
          console.error(
            "Invalid date string passed to setSelectedDate:",
            action.payload
          );
          state.selectedDate = new Date().toISOString();
        }
      } else {
        console.error(
          "Invalid payload type for setSelectedDate:",
          action.payload
        );
        state.selectedDate = new Date().toISOString();
      }
    },
    clearRecordings: (state) => {
      state.recordings = [];
    },
    setDateRange: (state, action) => {
      const { startDate, endDate } = action.payload;
      if (startDate) {
        state.startDate = startDate instanceof Date ? startDate.toISOString() : startDate;
      }
      if (endDate) {
        state.endDate = endDate instanceof Date ? endDate.toISOString() : endDate;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Dates
      .addCase(fetchRecordingDates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecordingDates.fulfilled, (state, action) => {
        state.loading = false;
        state.dates = action.payload;
      })
      .addCase(fetchRecordingDates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Recordings
      .addCase(fetchRecordings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecordings.fulfilled, (state, action) => {
        state.loading = false;
        state.recordings = action.payload || [];
      })
      .addCase(fetchRecordings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Recordings by Range
      .addCase(fetchRecordingsByRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecordingsByRange.fulfilled, (state, action) => {
        state.loading = false;
        state.recordings = action.payload || [];
      })
      .addCase(fetchRecordingsByRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Rate Recording
      .addCase(rateRecording.pending, (state) => {
        state.error = null;
      })
      .addCase(rateRecording.fulfilled, (state, action) => {
        const { filename, rating, notes } = action.payload;

        state.recordings = state.recordings.map((recording) =>
          recording.filename === filename
            ? { ...recording, rating, notes }
            : recording
        );
      })
      .addCase(rateRecording.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setSelectedDate, clearRecordings, setDateRange } = recordingsSlice.actions;
export default recordingsSlice.reducer;
